import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import UnitForm from "./UnitForm";
import UnitList from "./UnitList";
import ConfirmModal from "../../../../components/Modals/ConfirmModal";
import BackButton from "../../../../components/Button/BackButton";
import { toast } from "../../../../components/Toast/toast";

const UnitPage = () => {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get("action");
  const editId = searchParams.get("id");

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    try {
      const res = await api.get("/admin-units");
      setUnits(res.data.data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch units");
    } finally {
      setLoading(false);
    }
  };

  const setAction = (nextAction, id) => {
    const next = new URLSearchParams(searchParams);
    if (nextAction) next.set("action", nextAction);
    else next.delete("action");
    if (id !== undefined && id !== null) next.set("id", String(id));
    else next.delete("id");
    setSearchParams(next, { replace: true });
  };

  const handleSave = async (formData) => {
    try {
      if (action === "edit" && editId) {
        const res = await api.put(`/admin-units/${editId}`, formData);
        setUnits(
          units.map((u) => (String(u.id) === String(editId) ? res.data.data : u))
        );
      } else {
        const res = await api.post("/admin-units", formData);
        setUnits((prev) => [...prev, res.data.data]);
      }
      setAction(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to save unit");
    }
  };

  const handleDelete = async () => {
    if (!unitToDelete) return;
    try {
      await api.delete(`/admin-units/${unitToDelete}`);
      setUnits(units.filter((u) => u.id !== unitToDelete));
      setUnitToDelete(null);
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete unit");
    }
  };

  if (loading)
    return <div className="text-center mt-10 text-gray-500">Loading...</div>;

  const inForm = action === "add" || (action === "edit" && editId);
  const editingUnit =
    action === "edit" && editId
      ? units.find((u) => String(u.id) === String(editId))
      : null;

  return (
    <>
      <div className="md:w-1/2">
        <div className="flex justify-between items-center gap-3 mb-6">
          <div className="flex items-center gap-3">
            <BackButton
              onClick={inForm ? () => setAction(null) : undefined}
            />
            <h1 className="text-2xl font-bold text-gray-900">Units</h1>
          </div>
          {!inForm && (
            <button
              onClick={() => setAction("add")}
              className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
            >
              Add Unit
            </button>
          )}
        </div>

        {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

        {inForm ? (
          <UnitForm
            unitToEdit={editingUnit}
            onSave={handleSave}
            onCancel={() => setAction(null)}
          />
        ) : (
          <UnitList
            units={units}
            onEdit={(unit) => setAction("edit", unit.id)}
            onDelete={(id) => setUnitToDelete(id)}
          />
        )}
      </div>

      {unitToDelete && (
        <ConfirmModal
          title="Confirm Deletion"
          message="Are you sure you want to delete this unit? This action cannot be undone."
          onCancel={() => setUnitToDelete(null)}
          onConfirm={handleDelete}
          confirmText="Delete"
          confirmColor="bg-red-600"
          confirmHoverColor="hover:bg-red-700"
        />
      )}
    </>
  );
};

export default UnitPage;
